import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
	Device,
	DeviceAccessSource,
	isPdqDevice,
	isSentriVaultDevice,
} from 'src/app/models/data/device';
import { FeatureFlagName } from 'src/app/models/data/featureFlag';
import { SystemCapabilityEnum } from 'src/app/models/data/systemCapability';
import { UserProfile } from 'src/app/models/model';
import { ApiService, QueryParam } from 'src/app/services/api.services';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import DeviceInstallationPhoto = Components.Schemas.DeviceInstallationPhoto;
import { UserProfileService } from 'src/app/services/user-profile.service';
import { DeviceDetailsService } from 'src/app/services/device-details.service';
import { AUDIT_LOG_COLUMN_DEVICE } from 'src/app/app.constants';
import { AppTabNavigationHeaderComponent } from '../../../components/navigation-header/app-navigation-header.component';
import { MatTabGroup, MatTab, MatTabContent } from '@angular/material/tabs';
import { QueryStringTabGroupDirective } from '../../../directives/query-string-tab-group.directive';
import { NgClass } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { DeviceGenerateCodeComponent } from './device-generate-code/device-generate-code.component';
import { BreadcrumbLabelDirective } from '../../../directives/breadcrumb-label.directive';
import { DeviceAboutComponent } from './device-about/device-about.component';
import { DeviceNotesComponent } from './device-notes/device-notes.component';
import { UserAccessTabComponent } from '../../shared/user-access-tab/user-access-tab.component';
import { CompanyAccessTabComponent } from '../../shared/company-access/company-access.component';
import { DeviceEventHistoryComponent } from './device-event-history/device-event-history.component';
import { AuditLogListComponent } from '../../audit-log-list/audit-log-list.component';

export type DeviceUpdatedEvent = {
	deviceId: string;
	updated: boolean;
};

@Component({
	selector: 'app-device-details',
	host: {
		class: 'app-device-details full-height',
	},
	templateUrl: './device-details.component.html',
	styleUrls: ['./device-details.component.scss', '../../common/main.scss'],
	imports: [
		AppTabNavigationHeaderComponent,
		MatTabGroup,
		QueryStringTabGroupDirective,
		MatTab,
		MatTabContent,
		NgClass,
		MatProgressSpinner,
		DeviceGenerateCodeComponent,
		BreadcrumbLabelDirective,
		DeviceAboutComponent,
		DeviceNotesComponent,
		UserAccessTabComponent,
		CompanyAccessTabComponent,
		DeviceEventHistoryComponent,
		AuditLogListComponent,
	],
})
export class DeviceDetailsComponent {
	deviceId = this.route.snapshot.params.id;
	device!: Device;
	deviceInstallationPhotos: DeviceInstallationPhoto[] = [];
	userProfile!: UserProfile;
	canIssueFlexCodes: boolean = true;
	canViewGenerateCodes: boolean = true;
	canAccessEventHistory: boolean = false;
	canAccessAuditLogs: boolean = false;
	allowCodeGeneration: boolean = true; // set allowCodeGeneration and canIssueFlexCodes default to true so the tab loads
	hasUserRoleManagement: boolean = false;
	finishedLoadingAllowCodeGen: boolean = false;
	hasDeviceManagement: boolean = false;
	canViewDeviceAccessTabs: boolean = false;
	transferDeviceUpdatesFF: boolean = false;
	wasDeviceOwner?: boolean;
	isHaspCodeGenFeatureEnabled: boolean = false;
	isInstallPhotoManagementFeatureEnabled: boolean = false;

	// This will hold information on the device in the format that the common Company tab and
	// Grant Access slide out panel expects
	public devices: {
		device: Device;
		groupNames?: string[];
		source?: DeviceAccessSource;
	}[] = [];

	odcBreadcrumbLabel = '';
	aboutBreadcrumbLabel = '';
	deviceNotesBreadcrumbLabel = '';
	eventHistoryBreadcrumbLabel = '';
	auditLogBreadcrumbLabel = '';
	deviceCompaniesBreadcrumbLabel = '';
	deviceUsersBreadcrumbLabel = '';
	activeCompanyId: string | undefined = '';

	hiddenAuditLogColumns = [AUDIT_LOG_COLUMN_DEVICE];

	/** Passed to company/user access tabs — hide Allow Code Generation for PDQ device details. */
	hasOnlyPDQ = false;
	hasSentriVaultDevice = false;

	generateCodeLabel = $localize`:@@deviceDetails-generateCode:Generate Code`;
	detailsaboutLabel = $localize`:@@device-detailsabout:Details`;
	notesLabel = $localize`:@@deviceDetails-notes:Notes`;
	userAccessLabel = $localize`:@@device-userAccess:User Access`;
	companyAccessLabel = $localize`:@@device-companyAccess:Company Access`;
	eventHistoryLabel = $localize`:@@device-detailsAccessHistory:Access History`;
	auditLogLabel = $localize`:@@device-detailsAuditLog2:Audit Log`;

	get deviceName(): string {
		return this.device?.serialNumber ?? '';
	}

	constructor(
		private readonly route: ActivatedRoute,
		private readonly apiService: ApiService,
		private readonly scService: SystemCapabilityService,
		private readonly systemCapabilityService: SystemCapabilityService,
		private readonly profileService: UserProfileService,
		private readonly deviceDetailsService: DeviceDetailsService
	) {
		// DE4701: fetch the user role to 'User Access' tab from showing
		// if user doesn't have User & Role Management capabilities
		this.hasUserRoleManagement = this.scService.has(
			SystemCapabilityEnum.userRoleManagement
		);

		// allow to see Generate Codes tab with act or View capability
		this.canIssueFlexCodes = this.systemCapabilityService.has(
			SystemCapabilityEnum.issueFlexCodes
		);
		this.canViewGenerateCodes =
			this.systemCapabilityService.hasIssueFlexCodesView();

		this.hasDeviceManagement = this.scService.has(
			SystemCapabilityEnum.deviceManagement
		);
		this.canViewDeviceAccessTabs = this.scService.hasDeviceManagementView();

		this.fetchApiData().then((result: any) => {});
	}

	async fetchApiData() {
		// also check if the company owns the device, if not then do they have access to code generation on the device
		const queryParameters: QueryParam[] = [
			{ param: 'allowCodeGenerationBasedOnDeviceControl', value: true },
			{ param: 'allowCodeGenerationBasedOnStatus', value: true }, // this was changed today after talking to Elle. Leaving it incase needs to be added back in
		];

		// Call all at once to minimize time spend waiting for api calls
		const [
			allowCodeGeneration,
			deviceInstallationPhotos,
			device,
			userProfile,
			transferDeviceUpdatesFF,
			wasDeviceOwner,
			isHaspCodeGenFeatureEnabled,
			isInstallPhotoManagementFeatureEnabled,
		] = await Promise.all([
			this.apiService.allowCodeGeneration(this.deviceId, queryParameters),
			this.apiService.getDeviceInstallationPhotos(this.deviceId),
			this.deviceDetailsService.getDeviceWithCodeGenerationDetails(
				this.deviceId
			),
			this.profileService.getUserProfile(),
			this.apiService.getFeatureFlagByName(
				FeatureFlagName.transferDeviceUpdates
			),
			this.apiService.getWasDeviceOwner(this.deviceId),
			this.apiService.getFeatureFlagByName(FeatureFlagName.webHaspCodes),
			this.apiService.getFeatureFlagByName(
				FeatureFlagName.enableInstallPhotoManagement
			),
		]);
		this.userProfile = userProfile;
		this.allowCodeGeneration = allowCodeGeneration!;
		this.deviceInstallationPhotos = deviceInstallationPhotos;
		this.device = device;
		this.finishedLoadingAllowCodeGen = true;
		this.transferDeviceUpdatesFF = transferDeviceUpdatesFF.value;
		this.activeCompanyId = this.userProfile?.activeRoleDetails?.company?.id;
		this.wasDeviceOwner = wasDeviceOwner;
		this.isHaspCodeGenFeatureEnabled = isHaspCodeGenFeatureEnabled.value;
		this.isInstallPhotoManagementFeatureEnabled =
			isInstallPhotoManagementFeatureEnabled.value;

		this.updateEventHistoryAccess();
		await this.updateAuditLogAccess();

		// Setup information on the device in the format that the common Company tab and
		// Grant Access slide out panel expects.
		let source;
		if (this.device?.activeDeviceAssignment?.company) {
			source = {
				companyId: this.device?.activeDeviceAssignment?.company?.id,
				companyName: this.device?.activeDeviceAssignment?.company?.name,
			};
		}

		this.devices.push({ device: this.device, source });
		this.hasOnlyPDQ = isPdqDevice(this.device);
		this.hasSentriVaultDevice = isSentriVaultDevice(this.device);

		this.updateBreadcrumbLabels();
	}

	updateBreadcrumbLabels(): void {
		this.odcBreadcrumbLabel = `Device ${this.device?.serialNumber}`;
		this.aboutBreadcrumbLabel = `Device ${this.device?.serialNumber}`;
		this.deviceNotesBreadcrumbLabel = `Device ${this.device?.serialNumber}`;
		this.eventHistoryBreadcrumbLabel = `Device ${this.device?.serialNumber}`;
		this.auditLogBreadcrumbLabel = `Device ${this.device?.serialNumber}`;
		this.deviceCompaniesBreadcrumbLabel = `Device ${this.device?.serialNumber}`;
		this.deviceUsersBreadcrumbLabel = `Device ${this.device?.serialNumber}`;
	}

	async reloadDevice(): Promise<void> {
		const [device, deviceInstallationPhotos] = await Promise.all([
			this.apiService.getDeviceById(this.deviceId),
			this.apiService.getDeviceInstallationPhotos(this.deviceId),
		]);
		this.device = device;
		this.deviceInstallationPhotos = deviceInstallationPhotos;
	}

	// DE4763: if the user has Device Management (act or View), they should be able to view both the user and the company access tab
	canViewCompanyOrUserAccessTabs() {
		return (
			this.device !== undefined &&
			this.device.displayStatus !== 'expired' &&
			this.canViewDeviceAccessTabs
		);
	}

	private updateEventHistoryAccess(): void {
		if (this.systemCapabilityService.hasGcmView()) {
			this.canAccessEventHistory = true;
		} else if (
			this.systemCapabilityService.has(
				SystemCapabilityEnum.deviceManagementView
			)
		) {
			// Support with Device Management (View) bypasses owning-company gate
			this.canAccessEventHistory = true;
		} else if (
			this.systemCapabilityService.has(SystemCapabilityEnum.deviceManagement) &&
			(this.device.activeDeviceAssignment?.company?.id ===
				this.userProfile?.activeRoleDetails?.company.id ||
				(this.wasDeviceOwner && this.transferDeviceUpdatesFF))
		) {
			this.canAccessEventHistory = true;
		} else if (
			this.systemCapabilityService.has(SystemCapabilityEnum.runReports) &&
			this.isDeviceOwnedByCompany()
		) {
			this.canAccessEventHistory = true;
		}
	}

	private async updateAuditLogAccess(): Promise<void> {
		// Check if user has audit system capability
		const hasAuditCapability = this.systemCapabilityService.has(
			SystemCapabilityEnum.auditLogs
		);

		if (!hasAuditCapability) {
			this.canAccessAuditLogs = false;
			return;
		}

		// Check if user is SentriKey Access user OR has ownership over the device
		const isSentriKeyAccessUser = await this.checkIfSentriKeyAccessUser();
		const hasDeviceOwnership = this.isDeviceOwnedByCompany();

		this.canAccessAuditLogs = isSentriKeyAccessUser || hasDeviceOwnership;
	}

	private async checkIfSentriKeyAccessUser(): Promise<boolean> {
		try {
			const sentrilockId = await this.apiService.getSentrilockCompanyId();
			const activeCompanyId = this.userProfile?.activeRoleDetails?.company?.id;
			return activeCompanyId === sentrilockId;
		} catch (error) {
			return false;
		}
	}

	private isDeviceOwnedByCompany(): boolean {
		// For audit capability, user can only see event history if their company owns the device
		return (
			this.device?.activeDeviceAssignment?.company?.id ===
			this.userProfile?.activeRoleDetails?.company?.id
		);
	}
}
